import { isEqual } from 'lodash-es'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import type { AuditTrailEventDescriptionField, TaggingEventProps } from '../../model'
import { computeTaggingCodeDiffFields, getFieldLabel, getFieldType } from '../compute-field-diff'
import type { EventMeta } from '../pre-pass'
import { collectFieldSearchText } from './searchable-text-utils'

function buildTaggingFields({
  filteredEvents,
  event,
}: {
  filteredEvents: apiClient.AuditEvent[]
  event: apiClient.AuditEvent
}): {
  descriptionText: string | null
  fields: AuditTrailEventDescriptionField[] | null
  notes: string | null
} {
  const notes = event.notes?.replace(/^\[Mirror\] /, '') ?? null
  const usesNewTagsApi = ['tagsllm', 'tagsrev'].includes(event.event_type)
  const prevEventIndex =
    filteredEvents
      .map((e, i) => ({ event: e, index: i }))
      .filter(
        ({ event: e }) =>
          (e.event_type === event.event_type ||
            e.event_type === (usesNewTagsApi ? 'tagsllm' : 'datallm')) &&
          e.timestamp < event.timestamp &&
          e.data_content?.type === event.data_content?.type,
      )
      .pop()?.index ?? -1
  const prevEvent = prevEventIndex !== -1 ? filteredEvents[prevEventIndex] : undefined
  const dataFields = [...(event.data_content?.fields || [])]
  const prevDataFields = [...(prevEvent?.data_content?.fields || [])]
  if (dataFields.length > 0) {
    if (['datarev', 'tagsrev'].includes(event.event_type)) {
      const isFirstValidation = ['datallm', 'tagsllm'].includes(prevEvent?.event_type ?? '')
      // Edge case: if the tags are the same as the previous tags, then return 'Validated tags'
      // But only on revalidation — first validation should show all tags
      if (
        !isFirstValidation &&
        isEqual(
          dataFields
            .map((f) => f.value as (apiClient.CarcRarcCode | apiClient.TagInfo)[])
            .map((v) => v.map((v) => v.code))
            .sort(),
          prevDataFields
            .map((f) => f.value as (apiClient.CarcRarcCode | apiClient.TagInfo)[])
            .map((v) => v.map((v) => v.code))
            .sort(),
        )
      ) {
        return { descriptionText: 'Validated tags', fields: null, notes }
      }
    }
    const fields: AuditTrailEventDescriptionField[] = []
    dataFields.forEach((field) => {
      const fieldLabel = getFieldLabel(field.name)
      if (!fieldLabel) return
      const fieldType = getFieldType(field.name)
      const prevField = prevDataFields.find((f) => f.name === field.name)
      if (fieldType === 'carc' || fieldType === 'rarc' || fieldType === 'natera-tags') {
        const values = (field.value || []) as (apiClient.CarcRarcCode | apiClient.TagInfo)[]
        const prevValues = (prevField?.value || []) as (
          | apiClient.CarcRarcCode
          | apiClient.TagInfo
        )[]
        fields.push(
          ...computeTaggingCodeDiffFields({
            fieldLabel,
            fieldType,
            currentValues: values,
            prevValues,
            currentEventType: event.event_type,
            prevEventType: prevEvent?.event_type,
          }),
        )
      }
    })
    return {
      descriptionText: null,
      fields: fields.length > 0 ? fields : null,
      notes,
    }
  }
  return { descriptionText: null, fields: null, notes: null }
}

export function transformTagging(
  event: apiClient.AuditEvent,
  filteredEvents: apiClient.AuditEvent[],
  meta: EventMeta,
): TaggingEventProps {
  let title: string
  switch (event.event_type) {
    case 'datallm':
      title = 'Tagging: Tags extracted'
      break
    case 'datarev':
      title = meta.tagsRevalidated ? 'Tagging: Tags revalidated' : 'Tagging: Tags validated'
      break
    case 'tagsllm':
      title = 'Tagging: Tags extracted'
      break
    case 'tagsrev':
      title = meta.tagsRevalidated ? 'Tagging: Tags revalidated' : 'Tagging: Tags validated'
      break
    default:
      title = event.event_type
  }

  const result = buildTaggingFields({ filteredEvents, event })

  return {
    type: 'tagging',
    timestamp: event.timestamp,
    user: event.user,
    userRole: event.user ? 'person' : 'opti',
    step: 'tagging',
    eventType: event.event_type,
    title,
    descriptionText: result.descriptionText,
    fields: result.fields,
    notes: result.notes,
  }
}

export function getTaggingSearchableText(props: TaggingEventProps): string {
  const parts = [props.title]
  if (props.descriptionText) parts.push(props.descriptionText)
  if (props.fields) parts.push(collectFieldSearchText(props.fields))
  if (props.notes) parts.push(props.notes)
  return parts.join(' ')
}
